import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import { AssetFile, BoundingBox, Point } from './asset'
import { store } from '@/store'
import { AppModule } from '@/store/modules/app'
import { TreasureBook, Asset, CoverType, Gender} from '@/rest/models'
import { AddTreasureBookRequest } from '@/rest/apis'
import { TextWithSize } from '@/models/text'
import { TreasureBookTextComposer } from '@/components/products/schatzbuch/util/TreasureBookTextComposer'

export interface TreasureBookState {
    name: string
    userInputName: string
    dedication: TextWithSize
    userInputDedication: string
    innerImageAssetId: number
    innerImageBoundingBox: BoundingBox 
    innerImageRotation: number
    coverType: CoverType
    childGender: Gender
    parentGender: Gender
    text: string[]
    giftOption: boolean
}

export enum InnerImageState{
    DEFAULT,
    ASSET
}

export enum BackDetailState{
    NONE,
    TEXT
}

export class TreasureBookTransfer implements TreasureBook {

    public sessionId?: string
    public name: string
    public textBack?: TextWithSize
    public assetInside?: Asset
    public coverType: CoverType
    public parentGender: Gender
    public text: string[]
    public gift?: boolean
    
    constructor(sessionId: string | undefined, name: string, assetInside: Asset | undefined, coverType: CoverType, parentGender: Gender, text: string[]) {
        this.name = name
        this.sessionId = sessionId
        this.assetInside = assetInside
        this.coverType = coverType
        this.parentGender = parentGender
        this.text = text
    }

}

@Module({ dynamic: true, store, name: 'TreasureBook' })
class TreasureBookImpl extends VuexModule implements TreasureBookState {

    public name = ""
    public userInputName = ""
    public dedication = new TextWithSize("", 0)
    public userInputDedication = ""
    public innerImageAssetId = -1
    public innerImageBoundingBox = { ll: [0,0] as Point, ur: [0,0] as Point }
    public innerImageRotation = 0
    public innerImageState = InnerImageState.DEFAULT
    public coverType = CoverType.HardCover
    public childGender = Gender.None
    public parentGender = Gender.None
    public text: string[] = []
    public giftOption = false
    public backDetailState = BackDetailState.NONE

    @Mutation
    public SET_NAME(name: string) {
        this.name = name
        this.text = TreasureBookTextComposer.getText()
    }

    @Mutation
    public SET_USER_INPUT_NAME(userInputName: string) {
        this.userInputName = userInputName
    }

    @Mutation 
    public SET_DEDICATION(dedication: TextWithSize) {
        this.dedication = dedication
    }

    @Mutation 
    public SET_USER_INPUT_DEDICATION(userInputDedication: string) {
        this.userInputDedication = userInputDedication
    }

    @Mutation
    private SET_INNER_IMAGE_ASSET_ID(innerImageAssetId: number) {
        this.innerImageAssetId = innerImageAssetId
    }

    @Mutation
    private SET_INNER_IMAGE_BOUNDING_BOX(boundingBox: BoundingBox) {
        this.innerImageBoundingBox = boundingBox
    }

    @Mutation
    private SET_INNER_IMAGE_ROTATION(rotation: number) {
        this.innerImageRotation = rotation
    }

    @Mutation
    private SET_INNER_IMAGE_STATE(state: InnerImageState){
        this.innerImageState = state
    }

    @Mutation
    private SET_COVER_TYPE(coverType: CoverType){
        if (coverType === CoverType.HardCover){
            AppModule.SetProductID("80064")
        } else {
            AppModule.SetProductID("80070")
        }
        this.coverType = coverType

    }

    @Mutation
    private SET_CHILD_GENDER(childGender: Gender){
        this.childGender = childGender
        this.text = TreasureBookTextComposer.getText()
    }

    @Mutation
    private SET_PARENT_GENDER(parentGender: Gender){
        this.parentGender = parentGender
        this.text = TreasureBookTextComposer.getText()
    }

    @Mutation
    private SET_TEXT(text: string[]){
        this.text = text
    }

    @Mutation
    public SET_GIFT_OPTION(giftOption: boolean) {
        this.giftOption = giftOption
    }

    @Mutation
    private SET_BACK_DETAIL_STATE(state: BackDetailState){
        this.backDetailState = state
    }

    @Action({ commit: 'SET_NAME' })
    public SetName(name: string) {
        return name
    }

    @Action({ commit: 'SET_USER_INPUT_NAME' })
    public SetUserInputName(userInputName: string) {
        return userInputName
    }

    @Action({ commit: 'SET_DEDICATION' })
    public SetDedication(dedication: TextWithSize) {
        return dedication
    }

    @Action({ commit: 'SET_USER_INPUT_DEDICATION' })
    public SetUserInputDedication(userInputDedication: string) {
        return userInputDedication
    }

    @Action({ commit: 'SET_INNER_IMAGE_ASSET_ID' })
    public SetInnerImageAssetId(innerImageAssetId: number) {
        return innerImageAssetId
    }

    @Action({ commit: 'SET_INNER_IMAGE_BOUNDING_BOX' })
    public SetInnerImageBoundingBox(boundingBox: BoundingBox) {
        return boundingBox
    }

    @Action({ commit: 'SET_INNER_IMAGE_ROTATION' })
    public SetInnerImageRotation(rotation: number) {
        return rotation
    }

    @Action({ commit: 'SET_INNER_IMAGE_STATE' })
    public SetInnerImageState(state: InnerImageState) {
        return state
    }

    @Action({ commit: 'SET_COVER_TYPE' })
    public SetCoverType(coverType: CoverType) {
        return coverType
    }

    @Action({ commit: 'SET_CHILD_GENDER' })
    public SetChildGender(childGender: Gender) {
        return childGender
    }

    @Action({ commit: 'SET_PARENT_GENDER' })
    public SetParentGender(parentGender: Gender) {
        return parentGender
    }

    @Action({ commit: 'SET_TEXT' })
    public SetText(text: string[]) {
        return text
    }

    @Action({ commit: 'SET_GIFT_OPTION' })
    public SetGiftOption(giftOption: boolean) {
        return giftOption
    }

    @Action({ commit: 'SET_BACK_DETAIL_STATE' })
    public SetBackDetailState(state: BackDetailState) {
        return state
    }

    get isDefaultInnerImage() :boolean {
        return this.innerImageState == InnerImageState.DEFAULT
    }

    get treasureBookTransfer(): AddTreasureBookRequest {
        let innerAsset = undefined
        if(TreasureBookModule.innerImageAssetId !== -1) {
            innerAsset = {id: TreasureBookModule.innerImageAssetId,
                rotation: TreasureBookModule.innerImageRotation,
                ll: TreasureBookModule.innerImageBoundingBox.ll,
                ur: TreasureBookModule.innerImageBoundingBox.ur,
                pieces : "schatzbuchsoft"
            }
            if(TreasureBookModule.coverType == CoverType.HardCover) {
                innerAsset.pieces = "schatzbuchhard"
            }
        }
        
        let treasureBook = new TreasureBookTransfer(AppModule.session.sessionId, TreasureBookModule.name, innerAsset, TreasureBookModule.coverType, TreasureBookModule.parentGender, TreasureBookModule.text)
        
        treasureBook.textBack = new TextWithSize(TreasureBookModule.dedication.text, Math.round(TreasureBookModule.dedication.size * 1.38))

        treasureBook.gift = TreasureBookModule.giftOption

        let addTreasureBookRequest: AddTreasureBookRequest = {treasureBook: treasureBook}
        return addTreasureBookRequest;
    }

    /*change images*/

    get showDesignInnerImage(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "inner-image-upload" 
    }

    get showDesignBack(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "enter-dedication" 
    }
    
    get showDesignText(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "customize-text" 
    }
    
    get showDesignFront(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "enter-name" || step.componentName == "design-title" || step.componentName == "customize-parent" 
    }

    get showBackDetailText() :boolean {
        return this.backDetailState == BackDetailState.TEXT
    }

    get showToolbar(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "inner-image-upload"
    }
}

export const TreasureBookModule = getModule(TreasureBookImpl)
